"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

const MONTHS_EN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_ES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

// Example: simulated monthly card fees at a typical 2.6% rate on ~$23K card volume, slight seasonal variance
const TYPICAL_FEES = [598, 612, 621, 605, 634, 671, 698, 712, 645, 617, 689, 754];

export default function ZeroFeesYearChart() {
  const { lang, t } = useLang();
  const months = lang === "es" ? MONTHS_ES : MONTHS_EN;
  const total = TYPICAL_FEES.reduce((s, n) => s + n, 0);
  const max = Math.max(...TYPICAL_FEES);

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#00A876" }}>
            {t("12 months", "12 meses")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal max-w-[800px] mx-auto">
            {t("Card fees vs Redy. ", "Fees de tarjeta vs Redy. ")}
            <span className="text-redy-charcoal/30">{t("All year.", "Todo el año.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary max-w-[520px] mx-auto mt-6">
            {t(
              "An example year: what a typical 2.6% card rate takes out of your margin, month after month — and what comes out with Redy's dual pricing, where the card price covers it.",
              "Un año de ejemplo: lo que una tasa típica de tarjeta de 2.6% le saca a tu margen mes tras mes — y lo que sale con el dual pricing de Redy, donde el precio con tarjeta lo cubre."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white rounded-[32px] border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.04)] p-6 md:p-10 max-w-[920px] mx-auto"
        >
          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mb-8 text-[12px] font-bold">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-redy-charcoal/20" />
              <span className="text-redy-secondary">{t("Typical 2.6% card fees", "Fees típicos de 2.6%")}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#00C896" }} />
              <span style={{ color: "#00A876" }}>{t("Out of your margin with Redy", "De tu margen con Redy")}</span>
            </div>
          </div>

          {/* Bar chart */}
          <div className="grid grid-cols-12 gap-2 md:gap-3 h-[220px] md:h-[280px] items-end mb-3">
            {TYPICAL_FEES.map((fee, i) => {
              const heightPct = (fee / max) * 100;
              return (
                <div key={i} className="flex flex-col items-center gap-1 h-full justify-end">
                  {/* Typical card-fee bar (gray, full height-ish) */}
                  <motion.div
                    initial={{ height: 0 }}
                    whileInView={{ height: `${heightPct}%` }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                      duration: 0.7,
                      delay: 0.05 * i,
                      ease: [0.21, 0.47, 0.32, 0.98],
                    }}
                    className="w-full rounded-t-md bg-redy-charcoal/15 relative group cursor-default"
                  >
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-redy-charcoal text-white text-[10px] font-bold px-2 py-1 rounded whitespace-nowrap pointer-events-none">
                      ${fee}
                    </div>
                  </motion.div>
                  {/* Redy bar (green, 2px sliver — represents $0) */}
                  <div className="w-full h-[3px] rounded-sm" style={{ backgroundColor: "#00C896" }} />
                </div>
              );
            })}
          </div>

          {/* Month labels */}
          <div className="grid grid-cols-12 gap-2 md:gap-3 mb-10">
            {months.map((m) => (
              <div key={m} className="text-center text-[10px] md:text-[11px] font-bold text-redy-secondary/60 tracking-wider">
                {m}
              </div>
            ))}
          </div>

          {/* Total banner */}
          <div className="flex items-center justify-between bg-gradient-to-br from-[#00C896] to-[#00A876] rounded-[20px] px-6 py-5 text-white">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-white/70 mb-1">
                {t("Card fees covered this year (example)", "Fees de tarjeta cubiertos este año (ejemplo)")}
              </div>
              <div className="text-[36px] md:text-[48px] font-black leading-none tabular-nums">
                ${total.toLocaleString()}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-black uppercase tracking-widest text-white/70 mb-1">
                {t("At a typical 2.6% rate", "A una tasa típica de 2.6%")}
              </div>
              <div className="text-[18px] font-black tabular-nums line-through text-white/70">
                ${total.toLocaleString()}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
